import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Spinner, LoadingScreen } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import {
  companyProfileIsValid,
  validateCompanyProfile,
  type CompanyProfileErrors,
  type CompanyProfileInput,
} from '@/api/companyProfile';
import { needsOnboarding } from '@/api/onboarding';

function continuePath(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('/accept-terms')) {
    return from;
  }
  return '/dashboard';
}

export function AcceptTermsPage() {
  const { user, loading, completeProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [values, setValues] = useState<CompanyProfileInput>({
    firstName: user?.first_name ?? '',
    lastName: user?.last_name ?? '',
    companyName: user?.company_name ?? '',
  });
  const [errors, setErrors] = useState<CompanyProfileErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <LoadingScreen />;
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!needsOnboarding(user)) {
    return <Navigate to={continuePath(location.state)} replace />;
  }

  function onChange(field: keyof CompanyProfileInput, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;
    const nextErrors = validateCompanyProfile(values);
    setErrors(nextErrors);
    setFormError(null);
    if (!companyProfileIsValid(values)) return;

    setSubmitting(true);
    try {
      const { error } = await completeProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        companyName: values.companyName,
      });
      if (error) {
        setFormError(error.message);
        return;
      }
      navigate(continuePath(location.state), { replace: true });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page px-4 py-12">
      <form onSubmit={(event) => void onSubmit(event)} className="w-full max-w-[460px]" noValidate>
        <Card>
          <CardHeader className="space-y-5 px-8 py-8">
            <ProductBrand variant="light" showTagline={false} />
            <div>
              <h1 className="text-headline-md font-semibold text-foreground">Tell us about yourself</h1>
              <p className="mt-2 text-body-sm text-foreground-muted leading-relaxed">
                A few details before you get started with Sapyon AI.
              </p>
            </div>

            {formError ? (
              <div className="rounded-xl bg-error-subtle border border-error/20 px-3 py-2 text-body-sm text-error-ink">
                {formError}
              </div>
            ) : null}

            <div className="space-y-4">
              <ProfileField
                id="first-name"
                label="First name"
                placeholder="Enter your first name"
                autoComplete="given-name"
                value={values.firstName}
                error={errors.firstName}
                disabled={submitting}
                onChange={(value) => onChange('firstName', value)}
              />
              <ProfileField
                id="last-name"
                label="Last name"
                placeholder="Enter your last name"
                autoComplete="family-name"
                value={values.lastName}
                error={errors.lastName}
                disabled={submitting}
                onChange={(value) => onChange('lastName', value)}
              />
              <ProfileField
                id="company-name"
                label="Company name"
                placeholder="Enter your company name"
                autoComplete="organization"
                value={values.companyName}
                error={errors.companyName}
                disabled={submitting}
                onChange={(value) => onChange('companyName', value)}
              />
            </div>

            <Button type="submit" disabled={submitting} className="w-full h-12 text-body-md">
              {submitting ? <Spinner className="w-4 h-4" /> : null}
              Continue
            </Button>

            <p className="text-center text-body-sm text-foreground-muted leading-relaxed text-pretty">
              By continuing, you agree to Sapyon AI&apos;s{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap font-semibold text-brand-deep hover:text-brand hover:underline"
              >
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap font-semibold text-brand-deep hover:text-brand hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </CardHeader>
        </Card>
      </form>
    </div>
  );
}

function ProfileField({
  id,
  label,
  placeholder,
  autoComplete,
  value,
  error,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  autoComplete: string;
  value: string;
  error?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="block text-body-sm font-medium text-foreground mb-1">
        {label}
      </label>
      <Input
        id={id}
        name={id}
        type="text"
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={cn(error && 'border-error focus:border-error')}
      />
      {error ? (
        <p id={errorId} className="mt-1.5 text-body-sm text-error-ink">
          {error}
        </p>
      ) : null}
    </div>
  );
}
